/**
 * A workspace whose setup threw sits in 'error'. The maintenance sweep
 * retries it hourly for a day; this banner says so on the first-run pages
 * (Doors, Work's empty workspace) and lets the person retry now. After a
 * retry it reads the status every 10 seconds for up to 3 minutes: it hides
 * once the workspace is ready, and offers Retry again if setup failed again.
 */
import { useEffect, useState } from "react";

import { Button, Card } from "../ui/index.js";

type Phase = "hidden" | "failed" | "retrying" | "retry_failed" | "failed_again" | "slow";

export const SETUP_POLL_INTERVAL_MS = 10_000;
export const SETUP_POLL_WINDOW_MS = 3 * 60_000;

const MESSAGES: Record<Exclude<Phase, "hidden" | "retrying">, string> = {
  failed: "Setup didn't finish. We're retrying.",
  retry_failed: "The retry did not start. We try again every hour, or press Retry now.",
  failed_again: "Setup didn't finish again. We try again every hour, or press Retry again.",
  slow: "Setup is taking longer than usual. We try again every hour, or press Retry again.",
};

async function readStatus(): Promise<unknown> {
  try {
    const res = await fetch("/api/workspace/provisioning-status", { credentials: "include" });
    if (!res.ok) return null;
    return ((await res.json()) as { status?: unknown }).status ?? null;
  } catch {
    // No status to read (an older backend, or offline): treat as unknown.
    return null;
  }
}

export function SetupStatusBanner() {
  const [phase, setPhase] = useState<Phase>("hidden");

  useEffect(() => {
    let cancelled = false;
    void readStatus().then((status) => {
      if (!cancelled && status === "error") setPhase("failed");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // After a retry started: poll until ready, failed again, or out of time.
  useEffect(() => {
    if (phase !== "retrying") return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = Date.now() + SETUP_POLL_WINDOW_MS;
    const tick = async () => {
      const status = await readStatus();
      if (cancelled) return;
      if (status === "active") {
        setPhase("hidden");
        return;
      }
      if (status === "error") {
        setPhase("failed_again");
        return;
      }
      if (Date.now() + SETUP_POLL_INTERVAL_MS > deadline) {
        setPhase("slow");
        return;
      }
      timer = setTimeout(() => void tick(), SETUP_POLL_INTERVAL_MS);
    };
    timer = setTimeout(() => void tick(), SETUP_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [phase]);

  async function retry() {
    try {
      const res = await fetch("/api/workspace/retry-provisioning", { method: "POST", credentials: "include" });
      setPhase(res.ok ? "retrying" : "retry_failed");
    } catch {
      setPhase("retry_failed");
    }
  }

  if (phase === "hidden") return null;
  return (
    <Card tone="info" padding="sm" className="text-sm" role="status" data-testid="setup-status-banner">
      {phase === "retrying" ? (
        "Retrying setup. This takes about a minute."
      ) : (
        <>
          {MESSAGES[phase]}{" "}
          <Button size="sm" variant="secondary" onClick={() => void retry()} data-testid="setup-status-retry">
            {phase === "failed" || phase === "retry_failed" ? "Retry now" : "Retry again"}
          </Button>
        </>
      )}
    </Card>
  );
}
